import {useEffect, useState} from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import Layout from "@/components/Layout";
import { 
  Search, 
  Eye, 
  Zap, 
  Layers, 
  ArrowRight,
  Building,
  GraduationCap,
  Microscope,
  Plane,
  Shield,
  Camera
} from "lucide-react";
import { odCaseApi } from "@/api/modules/odCase";

// 定义案例数据类型
interface CaseItem {
  id: string;
  caseName: string;
  caseDesc: string;
  customer: string;
  coverPath: string;
  category: number;
  industry: number;
  challenge: string;
  achievement: string;
  content: string | null;
  tags: string;
}

const Cases = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [casesList, setCases] = useState<CaseItem[]>([]);
  const [selectedIndustry, setSelectedIndustry] = useState(-1);
  const [selectedProduct, setSelectedProduct] = useState(-1);

  const industries = [
    { id: -1, name: "全部行业", icon: <Building className="w-4 h-4" /> },
    { id: 1, name: "国防军事", icon: <Shield className="w-4 h-4" /> },
    { id: 2, name: "航空航天", icon: <Plane className="w-4 h-4" /> },
    { id: 3, name: "科研院所", icon: <Microscope className="w-4 h-4" /> },
    { id: 4, name: "高等院校", icon: <GraduationCap className="w-4 h-4" /> },
    { id: 5, name: "精密制造", icon: <Building className="w-4 h-4" /> },
    { id: 6, name: "光学仪器", icon: <Camera className="w-4 h-4" /> }
  ];

  const products = [
    { id: -1, name: "全部产品" },
    { id: 1, name: "几何光学仿真" },
    // { id: 2, name: "SeeLight" },
    { id: 3, name: "薄膜优化与误差分析" }
  ];

  /** 获取图片完整URL */
  const getImageUrl = (path: string | undefined): string => {
    if (!path) return '';
    // 如果已经是完整URL（http/https开头），直接返回
    if (path.startsWith('http://') || path.startsWith('https://')) {
      return path;
    }
    // 如果已经包含/file前缀，直接返回
    if (path.startsWith('/file/')) {
      return import.meta.env.VITE_API_BASE_URL + path;
    }
    // 否则添加/file前缀
    return import.meta.env.VITE_API_BASE_URL + '/file' + path;
  };
  /**
   * 获取案例列表
   */
  const fetchCases = async () => {
    try {
      const response = await odCaseApi.getOdCases({
        caseName: searchTerm || undefined,
        category: selectedProduct,
        industry: selectedIndustry,
      });
      setCases(response.rows || []);
    } catch (error) {
      console.error("获取案例失败:", error);
    }
  };

  useEffect(() => {
    fetchCases();
  }, [selectedIndustry, selectedProduct, searchTerm]);

  const getProductIcon = (category: number) => {
    switch (category) {
      case 1:
        return <Eye className="w-4 h-4" />;
      case 2:
        return <Zap className="w-4 h-4" />;
      case 3:
        return <Layers className="w-4 h-4" />;
      default:
        return <Building className="w-4 h-4" />;
    }
  };

  const getProductName = (category: number) => {
    switch (category) {
      case 1:
        return "几何光学仿真";
      case 2:
        return "SeeLight";
      case 3:
        return "薄膜优化与误差分析";
      default:
        return "未知产品";
    }
  };

  const getIndustryName = (industry: number) => {
    const found = industries.find(i => i.id === industry);
    return found ? found.name : "未知行业";
  };

  return (
    <Layout>
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-50 to-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <Badge variant="outline" className="mb-3 text-sm border-blue-300 text-blue-700 bg-blue-50">
              应用案例
            </Badge>
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              成功案例
            </h1>
            <p className="text-base text-gray-600 max-w-2xl mx-auto">
              与国内领先高等院校、研究院所以及行业龙头企业的合作案例，
              展示光学仿真软件在各个领域的成功应用
            </p>
          </div>

          {/* Search and Filters */}
          <div className="max-w-3xl mx-auto space-y-3">
            <div className="relative">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <Input
                type="text"
                placeholder="搜索案例..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-12 pr-4 py-2.5"
              />
            </div>
            
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="flex-1">
                <select
                  value={selectedIndustry}
                  onChange={(e) => setSelectedIndustry(Number(e.target.value))}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                >
                  {industries.map((industry) => (
                    <option key={industry.id} value={industry.id}>
                      {industry.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex-1">
                <select
                  value={selectedProduct}
                  onChange={(e) => setSelectedProduct(Number(e.target.value))}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                >
                  {products.map((product) => (
                    <option key={product.id} value={product.id}>
                      {product.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Cases Grid */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-6">
            <h2 className="text-xl font-semibold text-gray-900">
              找到 {casesList.length} 个案例
            </h2>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {casesList.map((caseItem) => (
              <Card key={caseItem.id} className="overflow-hidden hover:shadow-lg transition-shadow duration-300">
                <div className="aspect-video overflow-hidden">
                  <img 
                    src={getImageUrl(caseItem.coverPath)}
                    alt={caseItem.caseName}
                    className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                  />
                </div>
                <CardContent className="p-5">
                  <div className="flex items-center justify-between mb-3">
                    <Badge variant="outline" className="flex items-center space-x-1 text-xs">
                      {getProductIcon(caseItem.category)}
                      <span>{getProductName(caseItem.category)}</span>
                    </Badge>
                    <Badge variant="secondary" className="text-xs">
                      {getIndustryName(caseItem.industry)}
                    </Badge>
                  </div>

                  <h3 className="text-base font-semibold text-gray-900 mb-2">
                    {caseItem.caseName}
                  </h3>

                  <p className="text-gray-600 mb-3 leading-relaxed text-sm line-clamp-2">
                    {caseItem.caseDesc.replace(/SeeOD/g, "")}
                  </p>

                  <div className="space-y-2 mb-4">
                    <div>
                      <h4 className="font-medium text-gray-900 mb-1 text-sm">客户</h4>
                      <p className="text-xs text-gray-600">{caseItem.customer}</p>
                    </div>
                    <div>
                      <h4 className="font-medium text-gray-900 mb-1 text-sm">挑战</h4>
                      <p className="text-xs text-gray-600 line-clamp-1">{caseItem.challenge}</p>
                    </div>
                  </div>

                  <div className="space-y-1.5 mb-4">
                    <h4 className="font-medium text-gray-900 text-sm">主要成果</h4>
                    <ul className="space-y-1">
                      {caseItem.achievement.split('\n').slice(0, 2).map((result, idx) => (
                        <li key={idx} className="text-xs text-gray-600 flex items-center">
                          <div className="w-1.5 h-1.5 bg-blue-500 rounded-full mr-2 flex-shrink-0"></div>
                          <span className="line-clamp-1">{result}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {caseItem.tags && (
                    <div className="flex flex-wrap gap-1.5">
                      {caseItem.tags
                          .replace(/，/g, ',')  // 将中文逗号替换为英文逗号
                          .split(',')
                          .map((tag, idx) => (
                              <Badge key={idx} variant="outline" className="text-xs px-2 py-0.5">
                                {tag.trim()}
                              </Badge>
                          ))
                      }
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>

          {casesList.length === 0 && (
            <div className="text-center py-12">
              <Building className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <h3 className="text-base font-medium text-gray-900 mb-1.5">未找到相关案例</h3>
              <p className="text-gray-500 text-sm">
                请尝试调整搜索条件或筛选选项
              </p>
            </div>
          )}
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3">
              合作成果
            </h2>
            <p className="text-base text-gray-600">
              与合作伙伴共同创造的价值
            </p>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            <div className="text-center">
              <div className="text-3xl font-bold text-blue-600 mb-1.5">100+</div>
              <div className="text-gray-600 text-sm">合作客户</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-blue-600 mb-1.5">50+</div>
              <div className="text-gray-600 text-sm">成功案例</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-blue-600 mb-1.5">95%</div>
              <div className="text-gray-600 text-sm">客户满意度</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-blue-600 mb-1.5">24/7</div>
              <div className="text-gray-600 text-sm">技术支持</div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-blue-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
            成为我们的下一个成功案例
          </h2>
          <p className="text-base text-blue-100 mb-6">
            让我们的专业团队为您的项目提供定制化的解决方案
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Button size="default" variant="secondary" asChild>
              <Link to="/trial">
                免费试用 <ArrowRight className="ml-2 w-4 h-4" />
              </Link>
            </Button>
            <Button size="default" variant="outline" className="text-white border-white hover:bg-white hover:text-blue-600" asChild>
              <Link to="/contact">
                联系我们
              </Link>
            </Button>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default Cases;
